import { z } from 'zod'

import { sharedCookieDomain } from '@ui/lib/cookies'

import {
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_SEC,
  type ConsentRecord,
} from './cookie'
import { CONSENT_EVENT_NAMES, type ConsentDecisionAction } from './events'

const DECISION_ACTIONS = [
  'bannerAcceptAll',
  'bannerRejectAll',
  'preferencesAcceptAll',
  'preferencesRejectAll',
  'preferencesSave',
] as const satisfies readonly ConsentDecisionAction[]

export const consentRequestSchema = z.union([
  z.object({ source: z.literal('signal') }),
  z.object({ advertising: z.boolean(), action: z.enum(DECISION_ACTIONS) }),
])

export type ConsentRequest = z.infer<typeof consentRequestSchema>

// The Sec-GPC header decides `gpc`, never the body. Null: nothing to write.
export function consentDecision({
  body,
  signal,
  current,
}: {
  body: ConsentRequest
  signal: boolean
  current: ConsentRecord | undefined
}): { record: ConsentRecord; name: string } | null {
  if ('source' in body) {
    if (!signal || current?.gpc) return null
    return {
      record: { advertising: false, gpc: true },
      name: CONSENT_EVENT_NAMES.signalHonored,
    }
  }
  return {
    record: { advertising: body.advertising, gpc: signal },
    name: CONSENT_EVENT_NAMES[body.action],
  }
}

// writeCookie's attributes as a header. Not HttpOnly: the head script reads it.
export function consentSetCookie({
  record,
  host,
  secure,
}: {
  record: ConsentRecord
  host: string
  secure: boolean
}) {
  const domain = sharedCookieDomain(host.replace(/:\d+$/, ''))
  return [
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(record))}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE_SEC}`,
    'SameSite=Lax',
    ...(secure ? ['Secure'] : []),
    ...(domain ? [`Domain=${domain}`] : []),
  ].join('; ')
}
